import React, { useCallback, useEffect, useState } from "react";
import notifications from "../../../common/Components/Notifications/Notification";
import { canUseBillingSync } from "../../../helpers/billingSyncEnabled";
import { getZohoStatus, pushApprovalChallan, syncApprovalReturns } from "../../../services/billingSyncApi";

/** "15 Nov" (or "15 Nov 2027" outside this year) for a yyyy-MM-dd return-by date. */
export const returnByLabel = (value?: string) => {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
};

/** Today in India as yyyy-MM-dd, the earliest return-by date the server accepts. */
export const todayIst = () => {
  const now = new Date(Date.now() + (330 + new Date().getTimezoneOffset()) * 60000);
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

export type ApprovalChallan = {
  /** Zoho is switched on and connected, and this memo is out on approval. */
  on: boolean;
  busy: boolean;
  number: string;
  /** Pieces marked returned in HKP that Zoho's challan does not show yet. */
  pending: number;
  /** Returned pieces HKP could not match to a challan row: the studio records those in Zoho itself. */
  byHand: number;
  /** The approval is closed and Zoho's challan is closed too (delivered or returned). */
  settled: boolean;
  push: () => Promise<void>;
  syncReturns: () => Promise<void>;
};

/** Zoho delivery challan for an approval memo. The memo is read again after every Zoho call. */
export const useApprovalChallan = ({ memo, role, active, reload }: {
  memo: any;
  role?: string;
  active: boolean;
  reload: () => Promise<void>;
}): ApprovalChallan => {
  const allowed = active && canUseBillingSync(role);
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const memoId = memo?.id || "";

  useEffect(() => {
    if (!allowed) return;
    let live = true;
    getZohoStatus()
      .then((res: any) => live && setConnected(!!(res?.success && res?.data?.connected)))
      .catch(() => live && setConnected(false));
    return () => { live = false; };
  }, [allowed]);

  const run = useCallback(async (title: string, call: () => Promise<any>, done: (res: any) => void) => {
    if (busy || !memoId) return;
    setBusy(true);
    try {
      const res: any = await call();
      if (!res?.success) {
        notifications({ title, message: res?.message || "Try again", type: "danger" });
        return;
      }
      done(res);
    } catch {
      notifications({ title, message: "Check your connection and try again", type: "danger" });
    } finally {
      setBusy(false);
      await reload();
    }
  }, [busy, memoId, reload]);

  const push = useCallback(() => run("No delivery challan yet", () => pushApprovalChallan(memoId), (res) => {
    notifications({ title: "Delivery challan in Zoho", message: `${res?.data?.zohoChallanNumber || "The challan"} is ready to print from Zoho.`, type: "success" });
  }), [memoId, run]);

  const syncReturns = useCallback(() => run("Returns not yet in Zoho", () => syncApprovalReturns(memoId), () => {
    notifications({ title: "Returns recorded in Zoho", message: "The challan shows the pieces that came back.", type: "success" });
  }), [memoId, run]);

  return {
    on: allowed && connected,
    busy,
    number: memo?.zohoChallanNumber || "",
    pending: Number(memo?.zohoReturnsPending) || 0,
    byHand: Number(memo?.zohoReturnsByHand) || 0,
    settled: !!memo?.zohoChallanSettled,
    push,
    syncReturns,
  };
};

/** Returns not yet on the Zoho challan (a retry link), and ones the studio must record there by hand. */
const ChallanReturns = ({ challan, closed }: { challan: ApprovalChallan; closed?: boolean }) => (
  <>
    {challan.on && (challan.pending > 0 || (closed && !challan.settled)) ? (
      <div className="sp-row sp-hsn">
        <span className="t13 grow">
          {challan.pending > 0
            ? `${challan.pending} ${challan.pending === 1 ? "return is" : "returns are"} not on the Zoho challan yet`
            : "The Zoho challan is still open"}
        </span>
        <button type="button" className="sp-link sp-hsn__link" disabled={challan.busy} onClick={() => void challan.syncReturns()}>
          {challan.busy ? "Sending…" : challan.pending > 0 ? "Send to Zoho" : "Close it in Zoho"}
        </button>
      </div>
    ) : null}
    {challan.byHand > 0 ? (
      <div className="sp-warn">
        {challan.byHand} returned {challan.byHand === 1 ? "piece is" : "pieces are"} not on the Zoho challan rows. Record the return on challan {challan.number} in Zoho.
      </div>
    ) : null}
  </>
);

/** Out with the buyer: return-by date (changeable), overdue, and the Zoho challan. */
export const ApprovalOutPanel = ({ memo, canWrite, challan, onReturnBy }: {
  memo: any;
  canWrite: boolean;
  challan: ApprovalChallan;
  onReturnBy: (date: string) => Promise<boolean>;
}) => {
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(memo?.returnBy || "");
  const [saving, setSaving] = useState(false);
  const party = memo?.partyName || "the buyer";

  const save = async () => {
    if (!date || saving) return;
    setSaving(true);
    const ok = await onReturnBy(date);
    setSaving(false);
    if (ok) setEditing(false);
  };

  return (
    <>
      {memo?.overdue ? (
        <div className="sp-warn" role="alert">
          Overdue since {returnByLabel(memo.returnBy)}. Call {party}, then mark the pieces returned or kept.
        </div>
      ) : null}
      <div className="sp-info">
        <b>Out with {party}.</b> Tick the pieces that came back and tap <b>Mark returned</b>. When the buyer keeps the rest,
        tap <b>Keep the rest</b>: they move to a sale memo for billing.
      </div>
      {editing ? (
        <div className="sp-row sp-hsn">
          <label className="t13" htmlFor="sp-return-by">Return by</label>
          <input id="sp-return-by" className="sp-hsn__input" type="date" min={todayIst()} value={date}
            onChange={(e) => setDate(e.target.value)} />
          <button type="button" className="sp-hsn__save" disabled={saving || !date} onClick={() => void save()}>
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" className="sp-link sp-hsn__link" onClick={() => { setEditing(false); setDate(memo?.returnBy || ""); }}>Cancel</button>
        </div>
      ) : (
        <div className="sp-row sp-hsn">
          <span className="t13 grow">Return or confirm by <b>{returnByLabel(memo?.returnBy) || "no date"}</b></span>
          {canWrite ? <button type="button" className="sp-link sp-hsn__link" aria-label="Change return date" onClick={() => setEditing(true)}>Change</button> : null}
        </div>
      )}
      {challan.number ? (
        <>
          <div className="sp-row sp-hsn">
            <span className="t13 grow">Zoho delivery challan <b className="mono">{challan.number}</b></span>
          </div>
          <ChallanReturns challan={challan} />
        </>
      ) : challan.on && canWrite ? (
        <div className="sp-row sp-hsn">
          <span className="t13 grow">Need a delivery challan with the goods?</span>
          <button type="button" className="sp-link sp-hsn__link" disabled={challan.busy} onClick={() => void challan.push()}>
            {challan.busy ? "Talking to Zoho…" : "Create Zoho DC"}
          </button>
        </div>
      ) : null}
    </>
  );
};

/** Closed: what came back, what was kept, and the sale memo the kept pieces moved to. */
export const ApprovalClosedPanel = ({ memo, challan, onOpenSale }: {
  memo: any;
  challan: ApprovalChallan;
  onOpenSale: (id: string) => void;
}) => {
  const lines: any[] = memo?.lines || [];
  const returned = lines.filter((line) => line.lineStatus === "RETURNED").length;
  const kept = lines.filter((line) => line.lineStatus === "KEPT").length;
  return (
    <div className="sp-will">
      <div className="t16">Approval closed</div>
      <div><span className="ok">✓</span>{returned} {returned === 1 ? "piece" : "pieces"} back in stock</div>
      <div><span className="ok">✓</span>{kept} kept by {memo?.partyName || "the buyer"}</div>
      {challan.number ? (
        <div className="t13">Zoho delivery challan <b className="mono">{challan.number}</b>{challan.settled ? " · closed in Zoho" : ""}</div>
      ) : null}
      <ChallanReturns challan={challan} closed />
      {memo?.saleMemoId ? (
        <button type="button" className="sp-link" style={{ textAlign: "left" }} onClick={() => onOpenSale(memo.saleMemoId)}>
          Open sale memo <span className="mono">{memo.saleMemoCode}</span> to bill the kept pieces
        </button>
      ) : null}
    </div>
  );
};
